import { useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { fitLabel, type FittedLabel } from "./fit-label";
import { horizontalChrome, widthAroundLabel } from "./measure-free-width";
import {
  getFontsVersion,
  subscribeToFontLoads,
  textMeasurer,
} from "./measure-text";

/**
 * Where a label's room comes from:
 * - `label`: the label itself is capped at `maxWidth` (a chip).
 * - `trigger`: its `SelectorTrigger` is content-sized up to `maxWidth`.
 * - `observed`: it fills whatever the layout leaves it. Its own width is read,
 *   so it must not depend on the cut; the label keeps the full name in an
 *   invisible sizer for that.
 */
export type LabelSizing =
  | { mode: "label"; maxWidth: number }
  | { mode: "trigger"; maxWidth: number }
  | { mode: "observed" };

/** Optional parts of the label: the element holding the text (when it is not
 * the label box itself) and a suffix that shares the text's line. */
type LabelParts = {
  textElement?: HTMLElement | null;
  suffix?: HTMLElement | null;
  /** The invisible full-name copy an observed label renders while cut. */
  sizer?: HTMLElement | null;
};

const isClipped = (element: HTMLElement) =>
  element.scrollWidth > element.clientWidth;

function availableWidth(
  node: HTMLElement,
  sizing: LabelSizing,
  suffix: HTMLElement | null | undefined
) {
  // Not rendered (display: none, detached, or jsdom): never cut blind.
  if (node.getClientRects().length === 0) return;
  let room: number | undefined;
  if (sizing.mode === "observed") {
    room = node.offsetWidth - horizontalChrome(getComputedStyle(node));
  } else if (sizing.mode === "label") {
    room = sizing.maxWidth - horizontalChrome(getComputedStyle(node));
  } else {
    const combobox = node.closest<HTMLElement>('[role="combobox"]');
    const around = combobox ? widthAroundLabel(node, combobox) : undefined;
    room = around === undefined ? undefined : sizing.maxWidth - around;
  }
  return room === undefined ? undefined : room - (suffix?.offsetWidth ?? 0);
}

/**
 * Cuts `text` at a word boundary to the room `sizing` describes, and keeps it
 * cut as that room or the web font changes. Fits in a layout effect, so the
 * uncut text never paints. Undefined `text` (a non-text label) is left alone.
 */
export function useFittedLabel(
  node: HTMLElement | null,
  text: string | undefined,
  sizing: LabelSizing,
  parts: LabelParts = {}
): FittedLabel {
  // The cut text and the name it was cut from; null while nothing is cut.
  const [cut, setCut] = useState<{ source: string; text: string } | null>(null);
  const cutRef = useRef(cut);
  cutRef.current = cut;
  const fontsVersion = useSyncExternalStore(
    subscribeToFontLoads,
    getFontsVersion,
    getFontsVersion
  );
  const { mode } = sizing;
  const maxWidth = sizing.mode === "observed" ? undefined : sizing.maxWidth;
  const textElement = parts.textElement ?? node;
  const { suffix, sizer } = parts;

  useLayoutEffect(() => {
    if (!node || text === undefined) return;
    const current: LabelSizing =
      maxWidth === undefined || mode === "observed"
        ? { mode: "observed" }
        : { mode, maxWidth };

    const refit = () => {
      const isCut = cutRef.current?.source === text;
      // A whole name the browser would not clip is never cut. Uncut, ask the
      // text itself; cut in observed mode, ask the full-name sizer, so a box
      // that grows back uncuts. Capped modes take their room from the cap.
      let clippedWhole: boolean;
      if (!isCut) clippedWhole = textElement !== null && isClipped(textElement);
      else clippedWhole = sizer ? isClipped(sizer) : true;
      const available = clippedWhole
        ? availableWidth(node, current, suffix)
        : undefined;
      const measure = available === undefined ? undefined : textMeasurer(node);
      // One pixel under the room: canvas and layout can disagree by a fraction.
      const next =
        available !== undefined && measure
          ? fitLabel(text, Math.floor(available) - 1, measure)
          : { text, cut: false };
      setCut((previous) => {
        if (!next.cut) return previous?.source === text ? null : previous;
        return previous?.source === text && previous.text === next.text
          ? previous
          : { source: text, text: next.text };
      });
    };
    refit();

    // Safe to observe: in every mode the room is independent of the cut, so a
    // refit that resizes the label recomputes the same room and stops.
    const observer = new ResizeObserver(refit);
    observer.observe(node);
    if (suffix) observer.observe(suffix);
    const combobox =
      current.mode === "trigger"
        ? node.closest<HTMLElement>('[role="combobox"]')
        : null;
    if (combobox) observer.observe(combobox);
    return () => observer.disconnect();
  }, [node, text, mode, maxWidth, fontsVersion, textElement, suffix, sizer]);

  return cut && cut.source === text
    ? { text: cut.text, cut: true }
    : { text: text ?? "", cut: false };
}
